import { FC, useMemo } from 'react';
import { mainColors } from '../../../../styling/theme';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { Status } from '../../../../types/redux/data/dataTypes';
import {
  percentageToTwoDecimalPlaces,
  toTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import GenericStatusCell from '../../../tables/GenericStatusCell';
import SecondLevelTableWrapper from './SecondLevelTableWrapper.component';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useStressScenarioTestsPageModel } from '../../../../api/rs-hg/risk/hooks/queries';
import type {
  StressScenarioPageData,
  StressTestsPositions,
} from './stressScenario.types';

export type { StressTestsPositions };

export interface StressTestsData {
  headerRow: boolean;
  stressScenarioType: string | null;
  name: string | null;
  pL: number | null;
  pLstatus: Status | null;
  exAnteVolatility: number | null;
  exAnteVar: number | null;
  numSds: number | null;
  numVar: number | null;
  exPostVolatility: number | null;
  exPostVar: number | null;
  positions?: StressTestsPositions[];
}

export const mapStressTestNames = (name: string) => {
  switch (name) {
    case 'Sept2008':
      return 'Sept2008 - Global Financial Crisis';
    case 'Oct2008':
      return 'Oct2008 - Global Financial Crisis Escalates';
    case 'Apr2009':
      return 'Apr2009 - Global Financial Crisis Recovery';
    case 'July2011':
      return 'July2011 - Eurozone Sovereign Debt Crisis';
    case 'Aug2011':
      return 'Aug2011 - Eurozone Crisis Redux';
    case 'Mar2020':
    case 'March2020':
      return 'March2020 - Covid Crisis';
    default:
      return name;
  }
};

export const buildStressColumns = (): CustomColumn<StressTestsData>[] => {
  return [
    {
      title: 'Stress/Scenario Type',
      field: 'stressScenarioType',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: (rowData, rowDataValues) =>
        rowDataValues.headerRow
          ? {
            padding: '1.25rem',
            fontWeight: 700,
            color: mainColors.mainBlue,
            textAlign: 'center',
          }
          : {
            padding: 0,
          },
    },
    {
      title: 'Name',
      field: 'name',
      cellStyle: {
        padding: 0,
      },
      headerStyle: {},
      render: (rowData) => mapStressTestNames(rowData.name!),
    },
    {
      title: 'P/L',
      field: 'pL',
      cellStyle: {
        textAlign: 'center',
        padding: 0,
      },
      headerStyle: {
        textAlign: 'center',
      },
      pdfRenderType: 'StatusWithPercentage',
      renderMethod: {
        methodName: 'percentageToTwoDecimalPlaces',
        params: ['pL'],
      },
      render: (rowData) => {
        const statusCell = rowData.headerRow ? (
          ''
        ) : (
          <GenericStatusCell
            status={rowData.pLstatus!}
            innerText={percentageToTwoDecimalPlaces(rowData.pL!)}
            height="3.5rem"
          />
        );
        return statusCell;
      },
    },
    {
      title: 'Ex-Ante Volatility',
      field: 'exAnteVolatility',
      cellStyle: {
        textAlign: 'center',
        padding: 0,
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        rowData.headerRow
          ? ''
          : percentageToTwoDecimalPlaces(rowData.exAnteVolatility as number),
    },
    {
      title: 'Ex-Ante VaR',
      field: 'exAnteVar',
      cellStyle: {
        textAlign: 'center',
        padding: 0,
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        rowData.headerRow
          ? ''
          : percentageToTwoDecimalPlaces(rowData.exAnteVar as number),
    },
    {
      title: '#SDs',
      field: 'numSds',
      cellStyle: {
        textAlign: 'center',
        padding: 0,
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        rowData.headerRow ? '' : toTwoDecimalPlaces(rowData.numSds as number),
    },
    {
      title: '#VaR',
      field: 'numVar',
      cellStyle: {
        textAlign: 'center',
        padding: 0,
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        rowData.headerRow ? '' : toTwoDecimalPlaces(rowData.numVar as number),
    },
    {
      title: 'Ex-Post Volatility',
      field: 'exPostVolatility',
      cellStyle: {
        textAlign: 'center',
        padding: 0,
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        rowData.headerRow
          ? ''
          : percentageToTwoDecimalPlaces(rowData.exPostVolatility as number),
    },
    {
      title: 'Ex-Post VaR',
      field: 'exPostVar',
      cellStyle: {
        textAlign: 'center',
        padding: 0,
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        rowData.headerRow
          ? ''
          : percentageToTwoDecimalPlaces(rowData.exPostVar as number),
    },
  ];
};

export const sectorNameMap: { [key: string]: string } = {
  risk_sector_stress_tests: 'Risk Sector Stress',
  historical_stress_tests: 'Historical Stress',
  relative_stress_tests: 'Relative Stress',
};

export function buildStressTestData(
  pageData: StressScenarioPageData | null | undefined,
): StressTestsData[] {
  if (!pageData?.stress_test_data || !pageData?.stress_test_restrictions) {
    return [];
  }

  try {
    const mainData = pageData.stress_test_data;
    const nav = mainData.nav;
    const restrictions = pageData.stress_test_restrictions;
    const returnArr: StressTestsData[] = [];

    (
      [
        'risk_sector_stress_tests',
        'historical_stress_tests',
        'relative_stress_tests',
      ] as const
    ).forEach((testCategory) => {
      const tests = mainData[testCategory];
      if (!tests?.length) {
        return;
      }

      returnArr.push({
        headerRow: true,
        stressScenarioType: sectorNameMap[testCategory],
        name: null,
        pL: null,
        pLstatus: null,
        exAnteVolatility: null,
        exAnteVar: null,
        numSds: null,
        numVar: null,
        exPostVolatility: null,
        exPostVar: null,
      });

      tests.forEach((test) => {
        const name = test[0];
        const dataValuesForOtherColumns = test[1];
        const pLvalue = dataValuesForOtherColumns[0] / nav;

        returnArr.push({
          headerRow: false,
          stressScenarioType: null,
          name,
          pL: pLvalue,
          pLstatus:
            pLvalue < 0 &&
              Math.abs(pLvalue) >= (restrictions[name] ?? Infinity)
              ? Status.Alert
              : Status.Pass,
          exAnteVolatility: dataValuesForOtherColumns[1] / nav,
          exAnteVar: dataValuesForOtherColumns[2] / nav,
          numSds: dataValuesForOtherColumns[3],
          numVar: dataValuesForOtherColumns[4],
          exPostVolatility: dataValuesForOtherColumns[5] / nav,
          exPostVar: dataValuesForOtherColumns[6] / nav,
          positions: mainData.position_level_breakdown?.[name] ?? undefined,
        });
      });
    });

    return returnArr;
  } catch (err) {
    console.error('Problem building stress data: ', err);
  }

  return [];
}

const StressScenarioPage: FC<FundInfoComponentProps> = ({ fundId, positionDate }) => {
  const { data, isLoading, isFetching, isError, error } =
    useStressScenarioTestsPageModel(fundId, positionDate);

  const columns = useMemo(() => buildStressColumns(), []);

  return (
    <QueryBoundary
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={data?.data}
      loadingMessages={['Loading stress scenario tests…']}
      errorMessage="No stress scenario data available."
      noDataMessage="No stress scenario data available."
    >
      {(pageData) => {
        const tableData = buildStressTestData(pageData);

        return (
          <GridItem card xs={12}>
            <CustomTable<StressTestsData>
              pdfNoClearFirstRow
              options={{
                paging: false,
                exportButton: true,
              }}
              showToolbar={true}
              data={tableData}
              toolbarComponents={{
                toolbarTitle: data?.fund?.fund_name_full,
                titleStyle: {
                  fontWeight: 400,
                  fontSize: '1.25rem',
                  textTransform: 'capitalize',
                },
              }}
              columns={columns}
              csvFields={[
                'stressScenarioType',
                'name',
                'pL',
                'pLstatus',
                'exAnteVolatility',
                'exAnteVar',
                'numSds',
                'numVar',
                'exPostVolatility',
                'exPostVar',
              ]}
              detailPanel={SecondLevelTableWrapper()}
            />
          </GridItem>
        );
      }}
    </QueryBoundary>
  );
};

export default StressScenarioPage;
